// src/views/Admin/Companies/components/CompanyFormModal/CompanyFormModal.tsx

import React, { useState, useEffect } from 'react';
import { HiOutlinePlus, HiOutlinePencilSquare, HiOutlineXMark } from 'react-icons/hi2';
import './CompanyFormModal.scss';

export interface CompanyFormData {
  company_name: string;
  company_password: string;
  company_rut: string;
  company_email: string;
  company_phone: string;
  admin_name: string;
  address: string;
  state: boolean;
  company_code: string;
  plan_state: boolean;
  city_id: number;
}

interface CompanyFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: CompanyFormData;
  onClose: () => void;
  onSubmit: (data: CompanyFormData) => Promise<void>;
}

const defaultFormData: CompanyFormData = {
  company_name: '',
  company_password: '',
  company_rut: '',
  company_email: '',
  company_phone: '',
  admin_name: '',
  address: '',
  state: true,
  company_code: '',
  plan_state: true,
  city_id: 1
};

const CompanyFormModal: React.FC<CompanyFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<CompanyFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && initialData) {
      setFormData(initialData);
    } else if (isOpen) {
      setFormData(defaultFormData);
    }
  }, [isOpen, initialData]);

  const handleChange = (field: keyof CompanyFormData, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.company_name || !formData.company_email) {
      setError('Nombre y email son requeridos');
      return;
    }
    if (mode === 'create' && !formData.company_password) {
      setError('Contraseña es requerida');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Error al ${mode === 'create' ? 'crear' : 'actualizar'} compañía`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const isCreate = mode === 'create';

  return (
    <div className="form-modal-overlay" onClick={handleClose}>
      <div className="form-modal" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <button className="close-button" onClick={handleClose} disabled={isSubmitting}>
          <HiOutlineXMark size={20} />
        </button>

        <div className={`modal-icon ${isCreate ? 'create' : 'edit'}`}>
          {isCreate ? <HiOutlinePlus size={28} /> : <HiOutlinePencilSquare size={28} />}
        </div>

        <h2>{isCreate ? 'Nueva Compañía' : 'Editar Compañía'}</h2>

        {error && <div className="error-message">{error}</div>}

        <div className="form-grid">
          <div className="form-group full">
            <label>Nombre de la Compañía *</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="Ej: Restaurantes El Sol"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.company_email}
              onChange={(e) => handleChange('company_email', e.target.value)}
              placeholder="admin@empresa.com"
              disabled={isSubmitting}
            />
          </div>

          {isCreate && (
            <div className="form-group">
              <label>Contraseña *</label>
              <input
                type="password"
                value={formData.company_password}
                onChange={(e) => handleChange('company_password', e.target.value)}
                placeholder="Contraseña segura"
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="form-group">
            <label>RUT</label>
            <input
              type="text"
              value={formData.company_rut}
              onChange={(e) => handleChange('company_rut', e.target.value)}
              placeholder="12.345.678-9"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Teléfono</label>
            <input
              type="text"
              value={formData.company_phone}
              onChange={(e) => handleChange('company_phone', e.target.value)}
              placeholder="+56 9 1234 5678"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Nombre del Admin</label>
            <input
              type="text"
              value={formData.admin_name}
              onChange={(e) => handleChange('admin_name', e.target.value)}
              placeholder="Juan Pérez"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Código de Compañía</label>
            <input
              type="text"
              value={formData.company_code}
              onChange={(e) => handleChange('company_code', e.target.value)}
              placeholder="COMP001"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group full">
            <label>Dirección</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Av. Principal 123, Santiago"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Ciudad ID</label>
            <input
              type="number"
              value={formData.city_id}
              onChange={(e) => handleChange('city_id', parseInt(e.target.value) || 1)}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group toggles">
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={formData.state}
                onChange={(e) => handleChange('state', e.target.checked)}
                disabled={isSubmitting}
              />
              <span>Compañía Activa</span>
            </label>
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={formData.plan_state}
                onChange={(e) => handleChange('plan_state', e.target.checked)}
                disabled={isSubmitting}
              />
              <span>Plan Activo</span>
            </label>
          </div>
        </div>

        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button 
            className={`submit-button ${isCreate ? 'create' : 'edit'}`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner" />
                {isCreate ? 'Creando...' : 'Guardando...'}
              </>
            ) : (
              isCreate ? 'Crear Compañía' : 'Guardar Cambios'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyFormModal;
